import * as React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Alert, Collapse } from 'reactstrap';
import { getLanguage, useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import Typography from '../../../components/Typography';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { TextInputField } from '../../../components/Forms/fields';
import { ClientsLoginSettingsData, SelfSchedulingSettingsData } from '../../../models';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ProfessionalEntity } from '../../../store/entities/state';
import { serviceName } from '../../../shared/appointmint/appointment';
import AppButton from '../../../components/AppButton';

const PreviewGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--spacer-M);
`;

const PreviewToggleRow = styled.div`
    display: flex;
    justify-content: flex-start;
`;

const PreviewCard = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 12px;
    background: linear-gradient(180deg, white 0%, var(--surface-brand-soft) 100%);
    padding: var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const PreviewHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    padding-bottom: var(--spacer-XS);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
`;

const EmailLine = styled.div`
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--text-primary);
`;

const EmailCallout = styled.div`
    border-left: var(--border-thick) solid var(--brand-secondary);
    background-color: var(--surface-brand-soft);
    border-radius: 8px;
    padding: var(--spacer-S) var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
`;

const CalloutLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--brand-secondary);
`;

const PreviewTitle = styled(Typography)`
    margin: 0;
    color: var(--brand-secondary);
`;

const PreviewDetails = styled.div`
    display: grid;
    gap: var(--spacer-XS);
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 10px;
    background: white;
`;

const PreviewDetailRow = styled.div`
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: var(--spacer-S);
    align-items: baseline;
    font-size: 0.92rem;
`;

const PreviewDetailLabel = styled.span`
    color: var(--text-muted);
    font-weight: 600;
`;

const PreviewDetailValue = styled.span`
    color: var(--text-primary);
`;

const formatTemplate = (template: string | undefined, values: string[]) =>
    values.reduce((text, value, index) => text.replace(new RegExp(`\\{${index}\\}`, 'g'), value), template ?? '');

const previewTemplates = {
    en: {
        appointmentConfirmedClientEmailSubject: 'Appointment for {0} confirmed on {1} at {2} | {3}',
        appointmentConfirmedClientEmailBody: 'Hello {0}! Your appointment has been confirmed. You can find the details below.',
        appointmentPendingApprovalClientEmailGreeting: 'Hello {0}!',
        appointmentPendingApprovalClientEmailSubject: 'Appointment request for {0} registered on {1} at {2} | {3}',
        appointmentPendingApprovalClientEmailBody: '{0} is reviewing your {1} appointment request with {2} on {3} ({4}), at {5}.',
        appointmentPendingApprovalClientEmailClosing: 'You will receive confirmation soon.',
        appointmentPendingApprovalClientEmailThanks: 'Thank you and see you soon!',
    },
    pt: {
        appointmentConfirmedClientEmailSubject: 'Marcação de {0} confirmada dia {1} às {2} | {3}',
        appointmentConfirmedClientEmailBody: 'Olá {0}! A sua marcação foi confirmada. Pode consultar os detalhes abaixo.',
        appointmentPendingApprovalClientEmailGreeting: 'Olá {0}!',
        appointmentPendingApprovalClientEmailSubject: 'Pedido de {0} registado dia {1} às {2} | {3}',
        appointmentPendingApprovalClientEmailBody: 'A {0} está a analisar o seu pedido de marcação de {1} com {2} no dia {3} ({4}), às {5}.',
        appointmentPendingApprovalClientEmailClosing: 'Em breve receberá confirmação da mesma.',
        appointmentPendingApprovalClientEmailThanks: 'Obrigado e até já!',
    },
    fr: {
        appointmentConfirmedClientEmailSubject: 'Rendez-vous de {0} confirmé le {1} à {2} | {3}',
        appointmentConfirmedClientEmailBody: 'Bonjour {0} ! Votre rendez-vous a été confirmé. Vous trouverez les détails ci-dessous.',
        appointmentPendingApprovalClientEmailGreeting: 'Bonjour {0} !',
        appointmentPendingApprovalClientEmailSubject: 'Demande de {0} enregistrée le {1} à {2} | {3}',
        appointmentPendingApprovalClientEmailBody: "{0} examine votre demande de rendez-vous pour {1} avec {2} le {3} ({4}) à {5}.",
        appointmentPendingApprovalClientEmailClosing: 'Vous recevrez bientôt la confirmation.',
        appointmentPendingApprovalClientEmailThanks: 'Merci et à bientôt !',
    },
} as const;

const getPreviewTemplate = (
    currentLanguage: 'en' | 'pt' | 'fr',
    translatedTemplate: string | undefined,
    templateKey: keyof typeof previewTemplates.en,
) => {
    const fallbackTemplate = previewTemplates[currentLanguage][templateKey];
    if (!translatedTemplate || translatedTemplate === templateKey) {
        return fallbackTemplate;
    }

    if (currentLanguage !== 'en' && translatedTemplate === previewTemplates.en[templateKey]) {
        return fallbackTemplate;
    }

    return translatedTemplate;
};

interface NotificationsTabProps {
    id: string;
    values: ClientsLoginSettingsData;
    onSelfSchedulingChanged: (selfScheduling: Partial<SelfSchedulingSettingsData>) => void;
}

export const NotificationsTab: React.FC<NotificationsTabProps> = ({ id, values, onSelfSchedulingChanged }) => {
    const t = useTranslations();
    const currentLanguage = getLanguage();
    const generalSettings = useGeneralSettings();
    const professionals = useSelector(getEntitiesDataItems<ProfessionalEntity>('professionals'));
    const [previewOpen, setPreviewOpen] = React.useState(false);

    const appointmentType = React.useMemo(() => {
        const serviceKey = Object.keys(generalSettings.services ?? {})[0];
        return serviceKey ? serviceName(serviceKey, generalSettings) : t(TK.service);
    }, [generalSettings, t]);

    const previewDate = moment().add(7, 'days');
    const previewCompanyName = generalSettings.companyName ?? t(TK.appointmint);
    const previewProfessionalName = professionals[0]?.name ?? t(TK.professional);
    const previewDateText = previewDate.format('DD-MM-YYYY');
    const previewTimeText = '09:00';
    const previewDayText = previewDate.locale(currentLanguage).format('dddd');
    const previewCustomMessage = values?.selfScheduling?.pendingApprovalEmailCustomMessage?.trim() || t(TK.clientSelfSchedulingEmailPreviewPlaceholder);
    const pendingApprovalEnabled = values?.selfScheduling?.sendEmailWhenClientSelfSchedulesAndNotApprovedYet ?? true;

    const approvedSubject = formatTemplate(getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentConfirmedClientEmailSubject),
        'appointmentConfirmedClientEmailSubject',
    ), [
        appointmentType,
        previewDateText,
        previewTimeText,
        previewCompanyName,
    ]);
    const approvedPreview = formatTemplate(getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentConfirmedClientEmailBody),
        'appointmentConfirmedClientEmailBody',
    ), ['{client-name}']);
    const pendingGreeting = formatTemplate(getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentPendingApprovalClientEmailGreeting),
        'appointmentPendingApprovalClientEmailGreeting',
    ), ['{client-name}']);
    const pendingSubject = formatTemplate(getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentPendingApprovalClientEmailSubject),
        'appointmentPendingApprovalClientEmailSubject',
    ), [
        appointmentType,
        previewDateText,
        previewTimeText,
        previewCompanyName,
    ]);
    const pendingBody = formatTemplate(getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentPendingApprovalClientEmailBody),
        'appointmentPendingApprovalClientEmailBody',
    ), [
        previewCompanyName,
        appointmentType,
        previewProfessionalName,
        previewDateText,
        previewDayText,
        previewTimeText,
    ]);
    const pendingClosing = getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentPendingApprovalClientEmailClosing),
        'appointmentPendingApprovalClientEmailClosing',
    );
    const pendingThanks = getPreviewTemplate(
        currentLanguage,
        t(TK.appointmentPendingApprovalClientEmailThanks),
        'appointmentPendingApprovalClientEmailThanks',
    );
    const emailPreviewHelpText = `${t(TK.clientSelfSchedulingPendingApprovalCustomMessageHelp)}

${t(TK.clientSelfSchedulingPendingApprovalCustomMessagePlaceholders)}
- {client-name}
- {company-name}
- {first-appointment-date}
- {first-appointment-time}
- {professional-name}
- {appointment-type}
- {date}
- {time}`;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <Alert color="secondary" isOpen style={{ marginBottom: 0 }}>
                <Typography block style={{ margin: 0 }}>
                    {t(TK.clientSelfSchedulingEmailFlowDescription)}
                </Typography>
            </Alert>

            <CheckInputField
                id={`${id}-send-email-when-pending-approval`}
                label={t(TK.sendEmailWhenClientSelfSchedulesAndNotApprovedYet)}
                value={pendingApprovalEnabled}
                onChange={sendEmailWhenClientSelfSchedulesAndNotApprovedYet => onSelfSchedulingChanged({ sendEmailWhenClientSelfSchedulesAndNotApprovedYet })}
            />

            {pendingApprovalEnabled && (
                <TextInputField
                    id={`${id}-pending-approval-custom-message`}
                    label={t(TK.clientSelfSchedulingPendingApprovalCustomMessage)}
                    helpText={emailPreviewHelpText}
                    value={values?.selfScheduling?.pendingApprovalEmailCustomMessage ?? ''}
                    type="textarea"
                    rows={4}
                    onChange={pendingApprovalEmailCustomMessage => onSelfSchedulingChanged({ pendingApprovalEmailCustomMessage })}
                />
            )}

            <PreviewToggleRow>
                <AppButton color="link" onClick={() => setPreviewOpen(prev => !prev)} style={{ padding: 0 }}>
                    {previewOpen ? t(TK.clientSelfSchedulingEmailPreviewHide) : t(TK.clientSelfSchedulingEmailPreviewShow)}
                </AppButton>
            </PreviewToggleRow>

            <Collapse isOpen={previewOpen}>
                <div>
                    <PreviewTitle block size="large"><b>{t(TK.clientSelfSchedulingEmailPreviewTitle)}</b></PreviewTitle>
                    <PreviewGrid>
                        <PreviewCard>
                            <PreviewHeader>
                                <Typography block style={{ margin: 0, fontWeight: 600 }}>
                                    {t(TK.clientSelfSchedulingEmailPreviewPendingTitle)}
                                </Typography>
                            </PreviewHeader>
                            <EmailLine>
                                <strong>{t(TK.subject)}:</strong> {pendingSubject}
                            </EmailLine>
                            <EmailLine>{pendingGreeting}</EmailLine>
                            <EmailLine>{pendingBody}</EmailLine>
                            <EmailCallout>
                                <CalloutLabel>{t(TK.clientSelfSchedulingEmailPreviewPersonalizationLine)}</CalloutLabel>
                                <Typography block style={{ margin: 0 }}>
                                    {previewCustomMessage}
                                </Typography>
                            </EmailCallout>
                            <EmailLine>{pendingClosing}</EmailLine>
                            <EmailLine>{pendingThanks}</EmailLine>
                        </PreviewCard>

                        <PreviewCard>
                            <PreviewHeader>
                                <Typography block style={{ margin: 0, fontWeight: 600 }}>
                                    {t(TK.clientSelfSchedulingEmailPreviewApprovedTitle)}
                                </Typography>
                            </PreviewHeader>
                            <EmailLine>
                                <strong>{t(TK.subject)}:</strong> {approvedSubject}
                            </EmailLine>
                            <EmailLine>{approvedPreview}</EmailLine>
                            <PreviewDetails>
                                <PreviewDetailRow>
                                    <PreviewDetailLabel>{t(TK.date)}</PreviewDetailLabel>
                                    <PreviewDetailValue>{previewDate.format('DD-MM-YYYY')}</PreviewDetailValue>
                                </PreviewDetailRow>
                                <PreviewDetailRow>
                                    <PreviewDetailLabel>{t(TK.time)}</PreviewDetailLabel>
                                    <PreviewDetailValue>09:00</PreviewDetailValue>
                                </PreviewDetailRow>
                                <PreviewDetailRow>
                                    <PreviewDetailLabel>{t(TK.service)}</PreviewDetailLabel>
                                    <PreviewDetailValue>{appointmentType}</PreviewDetailValue>
                                </PreviewDetailRow>
                                <PreviewDetailRow>
                                    <PreviewDetailLabel>{t(TK.professional)}</PreviewDetailLabel>
                                    <PreviewDetailValue>{previewProfessionalName}</PreviewDetailValue>
                                </PreviewDetailRow>
                            </PreviewDetails>
                        </PreviewCard>
                    </PreviewGrid>
                </div>
            </Collapse>
        </div>
    );
};
